import { __decorate } from "tslib";
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import noop from 'lodash/noop';
import ViewComponent from '../core/ViewComponent';
import autobind from '../_util/autobind';
import Icon from '../icon';
import { ViewMode } from './enum';
import { FieldType } from '../data-set/enum';
import { $l } from '../locale-context';
import { stopEvent } from '../_util/EventManager';
export function alwaysValidDate() {
    return true;
}
export default class DaysView extends ViewComponent {
    getViewClassName() {
        return '';
    }
    render() {
        const { prefixCls, props: { className, extraFooterPlacement }, } = this;
        const classString = classNames(`${prefixCls}-view`, className, this.getViewClassName());
        return (React.createElement("div", { className: classString },
            this.renderHeader(),
            this.renderBody(),
            extraFooterPlacement === 'top' && this.customFooter,
            this.renderFooter(),
            extraFooterPlacement === 'bottom' && this.customFooter));
    }
    handlePrevYearClick() {
        this.changeSelectedDate(this.getCloneDate().subtract(1, 'y'), ViewMode.year);
    }
    handlePrevMonthClick() {
        this.changeSelectedDate(this.getCloneDate().subtract(1, 'M'), ViewMode.month);
    }
    handleMonthSelect() {
        this.changeViewMode(ViewMode.month);
    }
    handleYearSelect() {
        this.changeViewMode(ViewMode.year);
    }
    handleNextYearClick() {
        this.changeSelectedDate(this.getCloneDate().add(1, 'y'), ViewMode.year);
    }
    handleNextMonthClick() {
        this.changeSelectedDate(this.getCloneDate().add(1, 'M'), ViewMode.month);
    }
    handleKeyDownHome(e) {
        stopEvent(e);
        this.changeSelectedDate(this.getCloneDate().startOf('M'));
    }
    handleKeyDownEnd(e) {
        stopEvent(e);
        this.changeSelectedDate(this.getCloneDate().endOf('M'));
    }
    handleKeyDownLeft(e) {
        stopEvent(e);
        if (e.altKey) {
            this.changeViewMode(ViewMode.month);
        }
        else {
            this.changeSelectedDate(this.getCloneDate().subtract(1, 'd'));
        }
    }
    handleKeyDownRight(e) {
        stopEvent(e);
        if (!e.altKey) {
            this.changeSelectedDate(this.getCloneDate().add(1, 'd'));
        }
    }
    handleKeyDownUp(e) {
        stopEvent(e);
        this.changeSelectedDate(this.getCloneDate().subtract(1, 'w'));
    }
    handleKeyDownDown(e) {
        stopEvent(e);
        this.changeSelectedDate(this.getCloneDate().add(1, 'w'));
    }
    handleKeyDownPageUp(e) {
        stopEvent(e);
        this.changeSelectedDate(this.getCloneDate().subtract(1, e.altKey ? 'y' : 'M'));
    }
    handleKeyDownPageDown(e) {
        stopEvent(e);
        this.changeSelectedDate(this.getCloneDate().add(1, e.altKey ? 'y' : 'M'));
    }
    handleKeyDownEnter(e) {
        e.preventDefault();
        this.choose(this.props.date);
    }
    handleCellClick(date) {
        this.choose(date);
    }
    choose(date) {
        const { onSelect = noop } = this.props;
        onSelect(date);
    }
    changeSelectedDate(selectedDate, mode) {
        const { onSelectedDateChange = noop } = this.props;
        onSelectedDateChange(selectedDate, mode);
    }
    changeViewMode(mode) {
        const { onViewModeChange = noop } = this.props;
        onViewModeChange(mode);
    }
    renderHeader() {
        const { prefixCls, props: { date }, } = this;
        return (React.createElement("div", { className: `${prefixCls}-header` },
            React.createElement("a", { className: `${prefixCls}-prev-year`, onClick: this.handlePrevYearClick },
                React.createElement(Icon, { type: "first_page" })),
            React.createElement("a", { className: `${prefixCls}-prev-month`, onClick: this.handlePrevMonthClick },
                React.createElement(Icon, { type: "navigate_before" })),
            React.createElement("a", { className: `${prefixCls}-view-select`, onClick: this.handleMonthSelect }, date.localeData().monthsShort(date)),
            React.createElement("a", { className: `${prefixCls}-view-select`, onClick: this.handleYearSelect }, date.year()),
            React.createElement("a", { className: `${prefixCls}-next-year` },
                React.createElement(Icon, { type: "last_page", onClick: this.handleNextYearClick })),
            React.createElement("a", { className: `${prefixCls}-next-month`, onClick: this.handleNextMonthClick },
                React.createElement(Icon, { type: "navigate_next" }))));
    }
    renderBody() {
        return React.createElement("div", { className: `${this.prefixCls}-body` }, this.renderPanel());
    }
    renderPanel() {
        return (React.createElement("table", { className: this.getPanelClass(), cellSpacing: 0 },
            this.renderPanelHead(),
            React.createElement("tbody", null, this.renderPanelBody())));
    }
    renderPanelHead() {
        return (React.createElement("thead", null,
            React.createElement("tr", null, this.getDaysOfWeek())));
    }
    get customFooter() {
        const { prefixCls, props: { renderExtraFooter }, } = this;
        return renderExtraFooter ? (React.createElement("div", { className: `${prefixCls}-footer-extra` }, renderExtraFooter())) : null;
    }
    renderFooter() {
        const { prefixCls, props: { disabledNow }, } = this;
        const footerProps = {
            className: classNames({
                [`${prefixCls}-now-disabled`]: disabledNow,
            }),
            onClick: !disabledNow ? this.choose.bind(this, moment(), false) : noop,
        };
        return (React.createElement("div", { className: `${prefixCls}-footer` },
            React.createElement("a", Object.assign({}, footerProps), $l('DatePicker', 'today'))));
    }
    renderCell(props) {
        return React.createElement("td", Object.assign({}, props));
    }
    renderInner(text) {
        const { prefixCls } = this;
        return React.createElement("div", { className: `${prefixCls}-cell-inner` }, text);
    }
    getFirstDay(date) {
        const firstDay = date.clone().subtract(1, 'M');
        return firstDay.date(firstDay.daysInMonth()).startOf('w');
    }
    renderPanelBody() {
        const { prefixCls, props: { date, renderer = this.renderCell, isValidDate = alwaysValidDate }, } = this;
        const selected = date.clone();
        const prevMonth = this.getFirstDay(date);
        const currentYear = date.year();
        const currentMonth = date.month();
        const lastDay = prevMonth.clone().add(42, 'd');
        const rows = [];
        let cells = [];
        while (prevMonth.isBefore(lastDay)) {
            const currentDate = prevMonth.clone();
            const isDisabled = !isValidDate(currentDate, selected);
            const className = classNames(`${prefixCls}-cell`, {
                [`${prefixCls}-old`]: prevMonth.year() < currentYear ||
                    (prevMonth.year() === currentYear && prevMonth.month() < currentMonth),
                [`${prefixCls}-new`]: prevMonth.year() > currentYear ||
                    (prevMonth.year() === currentYear && prevMonth.month() > currentMonth),
                [`${prefixCls}-selected`]: prevMonth.isSame(selected, 'd'),
                [`${prefixCls}-today`]: prevMonth.isSame(moment(), 'd'),
                [`${prefixCls}-disabled`]: isDisabled,
            });
            const text = String(currentDate.date());
            const dayProps = {
                key: prevMonth.format('M_D'),
                className,
                children: this.renderInner(text),
            };
            if (!isDisabled) {
                dayProps.onClick = this.handleCellClick.bind(this, currentDate);
            }
            cells.push(renderer(dayProps, text, currentDate, selected));
            if (cells.length === 7) {
                rows.push(React.createElement("tr", { key: prevMonth.format('M_D') }, cells));
                cells = [];
            }
            prevMonth.add(1, 'd');
        }
        return rows;
    }
    getPanelClass() {
        return `${this.prefixCls}-day-panel`;
    }
    getDaysOfWeek() {
        const locale = this.props.date.localeData();
        const days = locale.weekdaysMin();
        const first = locale.firstDayOfWeek();
        const dow = [];
        let i = 0;
        days.forEach(day => {
            dow[(7 + i++ - first) % 7] = (React.createElement("th", { key: day, title: day }, day));
        });
        return dow;
    }
    getCloneDate() {
        return this.props.date.clone();
    }
}
DaysView.displayName = 'DaysView';
DaysView.propTypes = {
    date: PropTypes.object,
    renderer: PropTypes.func,
    isValidDate: PropTypes.func,
    onSelect: PropTypes.func,
    onSelectedDateChange: PropTypes.func,
    onViewModeChange: PropTypes.func,
    ...ViewComponent.propTypes,
};
DaysView.defaultProps = {
    suffixCls: 'calendar',
    extraFooterPlacement: 'bottom',
};
DaysView.type = FieldType.date;
__decorate([
    autobind
], DaysView.prototype, "handlePrevYearClick", null);
__decorate([
    autobind
], DaysView.prototype, "handlePrevMonthClick", null);
__decorate([
    autobind
], DaysView.prototype, "handleMonthSelect", null);
__decorate([
    autobind
], DaysView.prototype, "handleYearSelect", null);
__decorate([
    autobind
], DaysView.prototype, "handleNextYearClick", null);
__decorate([
    autobind
], DaysView.prototype, "handleNextMonthClick", null);
__decorate([
    autobind
], DaysView.prototype, "renderCell", null);
